// Imports
import React, { useContext, useMemo, useRef } from 'react';
import supportedBankAccountCountries, { isSupportedCountry } from './supported-countries';
import { Props as StripeBankAccountProps } from '.';
import { ThemeProvider } from 'styled-components/macro';
import ControlGroup from '../../../../../foundation/front-end/components/forms/control-group';
import FieldContainer from '../../../../../foundation/front-end/components/forms/field-container';
import FieldErrorMessage from '../../../../../foundation/front-end/components/forms/field-error-message';
import InputContainer from '../../../../../foundation/front-end/components/forms/input-container';
import { Label } from '../../../../../foundation/front-end/components/forms/labels';
import APIConnectedFormContext from '../../../../../contexts/api-connected-form';
import uniqueId from 'lodash/uniqueId';


// Define the accepted props
interface CompleteProps extends StripeBankAccountProps {
	routingNumberValue?: string;
	accountNumberValue?: string;
	handleAccountNumberChange?: React.ChangeEventHandler<HTMLInputElement>;
	handleRoutingNumberChange?: React.ChangeEventHandler<HTMLInputElement>;
	routingNumberError?: string;
	accountNumberError?: string;
	stripeError?: string;
	isLoading?: boolean;
}


// Function component
const BankAccountFields: React.FC<CompleteProps> = (props) => {
	// Break off the props we want to use
	const {
		accountNumberValue,
		routingNumberValue,
		handleAccountNumberChange,
		handleRoutingNumberChange,
		bankAccountDetails: { countryCode },
		accountNumber: accountNumberProp,
		routingNumber: routingNumberProp,
		errorMessage: errorMessageProp,
		name,
		routingNumberError,
		accountNumberError,
		stripeError,
		isLoading,
	} = props;
	
	
	// Throw if an unsupported country code was used
	if (!isSupportedCountry(countryCode)) {
		throw new Error('This country is not supported');
	}
	
	
	// Grab country
	const country = useMemo(() => supportedBankAccountCountries[countryCode], [countryCode]);
	
	
	// Use context
	const context = useContext(APIConnectedFormContext);
	
	
	// Retrieve values from API form's context, falling back to props
	const errorMessage = context?.error.field === name ? context.error.message : errorMessageProp;
	
	
	// Create themes
	const accountNumberTheme = {
		hasError: Boolean(accountNumberError || errorMessage || stripeError),
	};
	
	const routingNumberTheme = {
		hasError: Boolean(routingNumberError || errorMessage || stripeError),
	};
	
	
	// Generate unique ID
	const uniqueAccountNumberID = useRef(uniqueId('account-number-field-'));
	const uniqueRoutingNumberID = useRef(uniqueId('routing-number-field-'));
	
	
	// Return JSX
	return (
		<React.Fragment>
			{'routingNumber' in country && (
				<ThemeProvider theme={routingNumberTheme}>
					<FieldContainer>
						<Label htmlFor={uniqueRoutingNumberID.current}>
							{country.routingNumber.label}
							{routingNumberProp?.note && <small>{routingNumberProp?.note}</small>}
						</Label>
						
						<ControlGroup>
							<InputContainer>
								<input
									type='text'
									id={uniqueRoutingNumberID.current}
									value={routingNumberValue}
									onChange={handleRoutingNumberChange}
									minLength={country.routingNumber.min}
									maxLength={country.routingNumber.max}
									placeholder={isLoading ? 'Loading…' : country.routingNumber.placeholder}
									pattern={'pattern' in country.routingNumber ? country.routingNumber.pattern : undefined}
									data-intercom-target={
										props['data-intercom-target'] ? `${props['data-intercom-target']}-routing-number-field` : undefined
									}
									spellCheck='false'
									autoCorrect='off'
									data-private
									aria-invalid={Boolean(routingNumberError)}
									aria-describedby={`error-field-${uniqueRoutingNumberID.current}`}
									disabled={isLoading}
									required
								/>
							</InputContainer>
						</ControlGroup>
						
						{routingNumberError && (
							<FieldErrorMessage id={`error-field-${uniqueRoutingNumberID.current}`} message={routingNumberError} />
						)}
					</FieldContainer>
				</ThemeProvider>
			)}
			
			<ThemeProvider theme={accountNumberTheme}>
				<FieldContainer>
					<Label htmlFor={uniqueAccountNumberID.current}>
						{country.accountNumber.label}
						{accountNumberProp?.note && <small>{accountNumberProp?.note}</small>}
					</Label>
					
					<ControlGroup>
						<InputContainer>
							<input
								type='text'
								id={uniqueAccountNumberID.current}
								value={accountNumberValue}
								onChange={handleAccountNumberChange}
								minLength={country.accountNumber.min}
								maxLength={country.accountNumber.max}
								pattern={'pattern' in country.accountNumber ? country.accountNumber.pattern : undefined}
								placeholder={isLoading ? 'Loading…' : country.accountNumber.placeholder}
								data-intercom-target={
									props['data-intercom-target'] ? `${props['data-intercom-target']}-account-number-field` : undefined
								}
								spellCheck='false'
								autoCorrect='off'
								data-private
								aria-invalid={Boolean(errorMessage || accountNumberError || stripeError)}
								aria-describedby={`error-field-${uniqueAccountNumberID.current}`}
								disabled={isLoading}
								required
							/>
						</InputContainer>
					</ControlGroup>
					
					{(errorMessage || accountNumberError || stripeError) && (
						<FieldErrorMessage
							id={`error-field-${uniqueAccountNumberID.current}`}
							message={errorMessage || accountNumberError || stripeError || ''}
						/>
					)}
				</FieldContainer>
			</ThemeProvider>
		</React.Fragment>
	);
};

export default BankAccountFields;
